import { FileSearch } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { getCompany } from '@/api/companies-api';
import { useCompanyContextStore } from '@/store/company-context-store';

/**
 * Persistent banner shown while the active company is in `'full'` audit mode.
 * Stays visible across every page in the portal so the operator can't forget
 * the setting is on — payloads are being captured for every processed
 * message and storage cost scales with traffic.
 */
export function FullAuditBanner() {
  // One selector, never `useStore(a) ?? useStore(b)`: the `??` short-circuits
  // the second hook when a company is active, so switching tenants changed the
  // hook count and React unmounted the whole portal (black screen).
  const companyId = useCompanyContextStore((s) => s.activeCompanyId ?? s.companyId);

  const { data: company } = useQuery({
    queryKey: ['company-audit-mode', companyId],
    queryFn: () => (companyId ? getCompany(companyId) : Promise.resolve(null)),
    enabled: !!companyId,
    // Audit mode is a slow-moving setting; check every 60s to pick up changes
    // made from the company settings page.
    refetchInterval: 60_000,
    staleTime: 30_000,
  });

  if (!company || company.auditMode !== 'full') return null;

  return (
    <div className="flex items-center justify-between gap-4 border-b border-success/30 bg-success/10 px-4 py-2">
      <div className="flex items-center gap-3">
        <FileSearch className="h-4 w-4 shrink-0 text-success" />
        <div className="flex items-center gap-2 text-xs">
          <span className="font-medium text-success">Full Audit Mode Active</span>
          <span className="text-soft">
            &mdash; Original payloads are being stored for every processed message in this company
          </span>
        </div>
      </div>
    </div>
  );
}
